import React from 'react';
import { useIntl } from 'react-intl';
import { observer } from 'mobx-react';
import { EventHubActiveTab, ManageEventHubVM } from './manage-event-hub.viewmodel';
import { SubWindowsHost } from '../../global/sub-window/sub-windows-host';
import { EventHubAttendees } from './_event-hub-attendees/event-hub-attendees';
import { HubConfiguration } from './_hub-configuration/hub-configuration';
import { RestrictedContent } from '../../_restricted-content/restricted-content';
import DefaultButton from '../../global/default-button/default-button';
import { Permission } from '../../../api/roles.dao';
import { EventHubUrlSection } from './_event-hub-url-section/event-hub-url-section';
import { EventHubSubdomainForm } from './_event-hub-url-section/event-hub-subdomain-form/event-hub-subdomain.form';
import { EventHubsNavigation } from './shared/event-hubs-navigation';
import { EventHubMoreButton } from './shared/event-hub-more-button';
import { hasPermission } from '../../../infrastructure/utils/has-permissions';
import { NEW_HUB_ATTENDEE_TEXT, OPEN_BUILDER_BUTTON_TEXT } from '../event-hubs.text';

export type ManageEventHubPresentationProps = {
    vm: ManageEventHubVM;
};

export const ManageEventHubPresentation = observer(({vm}: ManageEventHubPresentationProps) => {
    const intl = useIntl();
    const accountId = vm.getAccountId();
    const eventHub = vm.getEventHub();

    if (!eventHub) {
        return null;
    }

    const attendeesVM = vm.getAttendeesVM();
    const isAttendeesTab = vm.getActiveTab() === EventHubActiveTab.Attendees;
    const canCreateAttendee = hasPermission(Permission.CREATE_HUB_ATTENDEES);
    const isConfigurationTab = vm.getActiveTab() === EventHubActiveTab.HubConfiguration;
    const canConfigureWebsite = hasPermission(Permission.HUB_WEBSITE_BUILDER);
    const websiteBuilderButtonAvailable = isConfigurationTab && canConfigureWebsite;

    let activeTabContent: JSX.Element;

    switch (vm.getActiveTab()) {
        case EventHubActiveTab.HubConfiguration:
            activeTabContent = <HubConfiguration vm={vm.getHubConfigurationVM()} />;
            break;
        case EventHubActiveTab.Attendees:
            activeTabContent = <RestrictedContent
                content={<EventHubAttendees vm={attendeesVM} />}
                permission={Permission.VIEW_HUB_ATTENDEES}
            />;
            break;
        default:
            activeTabContent = null;
    }

    const handleSaveCompleted = () => {
        vm.loadEventHubData();
        vm.closeManageUrlModal();
    };

    const canManageSubdomain = hasPermission(Permission.MANAGE_HUB_SUBDOMAIN);

    return (
        <SubWindowsHost>
            <div className='page-container'>
                <section className='top-page-title horizontal-row horizontal-row--with-bottom-margin'>
                    <h1 className='text-ellipsis'>{eventHub.eventHubName}</h1>
                    <EventHubUrlSection
                        subdomain={eventHub.subdomain}
                        onCreateUrl={vm.openManageUrlModal}
                    />
                </section>
                <EventHubsNavigation
                    actions={
                        <div className='buttons-block'>
                            {isAttendeesTab && canCreateAttendee &&
                                <DefaultButton
                                    modifier={['fat', 'confirm-safe']}
                                    text={intl.formatMessage(NEW_HUB_ATTENDEE_TEXT, { prefix: '+ ' })}
                                    onClick={attendeesVM.addAttendee}
                                />
                            }
                            {websiteBuilderButtonAvailable &&
                                <DefaultButton
                                    modifier={['fat', 'confirm-safe']}
                                    text={intl.formatMessage(OPEN_BUILDER_BUTTON_TEXT)}
                                    onClick={vm.openHubWebsiteBuilder}
                                />
                            }
                            <EventHubMoreButton onManageUrl={vm.openManageUrlModal} onNewOrganization={vm.openNewOrgPopup} />
                        </div>
                    }
                />
                {activeTabContent}
            </div>
            {canManageSubdomain && (
                <EventHubSubdomainForm
                    accountId={accountId}
                    eventHub={eventHub}
                    saveCompletedCallback={handleSaveCompleted}
                    isOpen={vm.getIsManageUrlModalOpen()}
                    onRequestClose={vm.closeManageUrlModal}
                />
            )}
        </SubWindowsHost>
    );
});